import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SeverityChip } from '@/components/SeverityChip';
import { Route as RouteIcon, Plus, Trash2, Pencil, X, Check, MapPin, Wand2 } from 'lucide-react';

const EMPTY = { name: '', threshold: 'yellow', highway_keys: [], geometry: null, start: null, destination: null, buffer_km: 8 };

const BUFFER_PRESETS = [
  { v: 3, l: 'Tight', d: '3 km · commuter' },
  { v: 8, l: 'Standard', d: '8 km · default' },
  { v: 15, l: 'Wide', d: '15 km · freight' },
];

export function RoutesManager({ highways, locked = false }) {
  const [routes, setRoutes] = useState([]);
  const [editing, setEditing] = useState(null); // 'new' | route id | null
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [geo, setGeo] = useState({ start: '', destination: '' });
  const [resolving, setResolving] = useState(false);

  const load = () => api.get('/routes').then((r) => setRoutes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setGeo({ start: '', destination: '' }); setEditing('new'); };
  const startEdit = (r) => { setForm({ name: r.name, threshold: r.threshold, highway_keys: [...r.highway_keys], geometry: r.geometry || null, start: r.start || null, destination: r.destination || null, buffer_km: r.buffer_km || 8 }); setGeo({ start: '', destination: '' }); setEditing(r.id); };
  const cancel = () => { setEditing(null); setForm(EMPTY); setGeo({ start: '', destination: '' }); };

  const resolveCorridor = async () => {
    if (!geo.start.trim() || !geo.destination.trim()) { toast.error('Enter a start and destination'); return; }
    setResolving(true);
    try {
      const { data } = await api.post('/routes/resolve-corridor', { start: geo.start.trim(), destination: geo.destination.trim() });
      setForm((f) => ({ ...f, highway_keys: data.highway_keys, geometry: data.geometry || null, start: geo.start.trim(), destination: geo.destination.trim(), name: (f.name || '').trim() ? f.name : data.suggested_name }));
      toast.success(`Found ${data.highway_keys.length} highway${data.highway_keys.length === 1 ? '' : 's'} · ${data.distance_km} km · route geo-fenced`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build corridor from those places');
    } finally { setResolving(false); }
  };

  const toggleHw = (key) => {
    const s = new Set(form.highway_keys);
    if (s.has(key)) s.delete(key); else s.add(key);
    setForm({ ...form, highway_keys: Array.from(s) });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give your route a name'); return; }
    if (form.highway_keys.length === 0) { toast.error('Select at least one highway'); return; }
    setSaving(true);
    try {
      if (editing === 'new') { await api.post('/routes', form); toast.success('Route created'); }
      else { await api.patch(`/routes/${editing}`, form); toast.success('Route updated'); }
      await load(); cancel();
    } catch (e) { toast.error('Could not save route'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this route?')) return;
    try { await api.delete(`/routes/${id}`); toast.success('Route deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const hwLabel = (key) => key.replace('N-', '').replace('S-', '') + (key.startsWith('N-') ? ' N' : ' S');

  if (locked) {
    return (
      <div className="card mb-6" data-testid="routes-locked">
        <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-1"><RouteIcon className="w-5 h-5 text-teal-600" /> My Routes</h2>
        <p className="text-sm text-zinc-500">Route/Corridor Watch is a <strong>Plus</strong> feature. Upgrade to save the corridors you travel (commute or freight lane) and get targeted alerts.</p>
      </div>
    );
  }

  return (
    <div className="card mb-6" data-testid="routes-manager">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><RouteIcon className="w-5 h-5 text-teal-600" /> My Routes</h2>
        {editing === null && (
          <button onClick={startNew} className="btn btn-primary" data-testid="route-new"><Plus className="w-4 h-4" /> New route</button>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-4">Save the corridors you travel — a commute or a freight lane — and get alerts only for those highways, at their own severity level.</p>

      {editing !== null && (
        <div className="rounded-xl border-2 border-teal-200 bg-teal-50/40 p-4 mb-4" data-testid="route-editor">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label">Route name</label>
              <input className="input" placeholder="e.g. Wellington → Palmerston North" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="route-name" />
            </div>
            <div>
              <label className="label">Alert level for this route</label>
              <div className="flex gap-2">
                {[{ v: 'red', l: 'Critical' }, { v: 'orange', l: 'Urgent+' }, { v: 'yellow', l: 'All' }].map((o) => (
                  <button key={o.v} type="button" onClick={() => setForm({ ...form, threshold: o.v })} className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border-2 transition ${form.threshold === o.v ? 'border-teal-500 bg-white' : 'border-zinc-200 bg-white/60 text-zinc-500'}`} data-testid={`route-threshold-${o.v}`}>{o.l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-teal-200 bg-white p-3 mb-4" data-testid="corridor-autofill">
            <div className="text-xs font-semibold uppercase tracking-wider text-teal-700 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Auto-build from start → destination</div>
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <input className="input" placeholder="Start (e.g. Christchurch)" value={geo.start} onChange={(e) => setGeo({ ...geo, start: e.target.value })} data-testid="corridor-start" />
              <input className="input" placeholder="Destination (e.g. Ashburton)" value={geo.destination} onChange={(e) => setGeo({ ...geo, destination: e.target.value })} data-testid="corridor-destination" />
            </div>
            <button type="button" onClick={resolveCorridor} disabled={resolving} className="btn btn-secondary" data-testid="corridor-resolve">
              <Wand2 className="w-4 h-4" /> {resolving ? 'Finding highways…' : 'Find highways on this route'}
            </button>
            <p className="text-[11px] text-zinc-400 mt-2">We geocode both places and detect the State Highways your drive uses, then pre-select them below. Within-island routes only (Cook Strait can't be auto-routed).</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-3 mb-4" data-testid="route-buffer">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-600 mb-2">Alert radius {form.geometry && form.geometry.length ? '' : '(applies once route is geo-fenced)'}</div>
            <div className="flex gap-2">
              {BUFFER_PRESETS.map((o) => (
                <button key={o.v} type="button" onClick={() => setForm({ ...form, buffer_km: o.v })} className={`flex-1 px-3 py-2 rounded-lg border-2 transition text-center ${form.buffer_km === o.v ? 'border-teal-500 bg-teal-50' : 'border-zinc-200 bg-white text-zinc-500'}`} data-testid={`route-buffer-${o.v}`}>
                  <div className="text-xs font-bold">{o.l}</div>
                  <div className="text-[10px] text-zinc-400">{o.d}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400 mt-2">How far off your drive an event can be and still alert you. Tighter = fewer, more relevant alerts; wider = catches nearby detours.</p>
          </div>
          <label className="label">Highways on this route</label>
          <div className="grid md:grid-cols-2 gap-4">
            {['north', 'south'].map((ik) => {
              const list = highways?.[ik] || [];
              const label = ik === 'north' ? 'North Island' : 'South Island';
              const emoji = ik === 'north' ? '🌋' : '🏔️';
              return (
                <div key={ik} className="p-3 rounded-lg border border-zinc-200 bg-white">
                  <div className="font-display font-bold text-sm mb-2">{emoji} {label}</div>
                  <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                    {list.map((h) => {
                      const on = form.highway_keys.includes(h.key);
                      return (
                        <button key={h.key} type="button" onClick={() => toggleHw(h.key)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${on ? 'bg-teal-500 border-teal-500 text-black' : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400'}`} data-testid={`route-hw-${h.key}`}>{h.highway}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-zinc-500">{form.highway_keys.length} highways selected</div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} disabled={saving} className="btn btn-primary" data-testid="route-save"><Check className="w-4 h-4" /> {saving ? 'Saving…' : 'Save route'}</button>
            <button onClick={cancel} className="btn btn-ghost" data-testid="route-cancel"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}

      {routes.length === 0 && editing === null ? (
        <p className="text-sm text-zinc-400 py-2">No routes yet. Click "New route" to create your first corridor watch.</p>
      ) : (
        <div className="space-y-2">
          {routes.map((r) => (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-zinc-100" data-testid={`route-row-${r.id}`}>
              <SeverityChip severity={r.threshold} outline />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.name}</div>
                <div className="text-xs text-zinc-500 truncate">{r.highway_keys.map(hwLabel).join(', ') || 'No highways'}</div>
              </div>
              {r.geometry && r.geometry.length ? (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5" title="Alerts are limited to events along your actual drive">
                  <MapPin className="w-3 h-3" /> Geo-fenced · {r.buffer_km || 8} km
                </span>
              ) : (
                <span className="hidden sm:inline-flex items-center text-[10px] font-semibold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5" title="Matches every event on these highway numbers. Re-build with start → destination to geo-fence.">
                  Highway-only
                </span>
              )}
              <button onClick={() => startEdit(r)} className="p-2 hover:bg-zinc-100 text-zinc-600 rounded-lg" data-testid={`route-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(r.id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg" data-testid={`route-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
